<template>
	<ValidationProvider
		:rules="validationRules"
		:name="name || label"
		:vid="validationId"
		v-slot="{ errors }"
		tag="div"
		class="currency-field-container"
	>
		<header :class="['input-header', { 'is-small': isSmall }]">
			<label :for="inputId" :class="{ 'has-error': errors.length > 0 }">
				{{ label }}
				<span v-if="isOptional && !disabled"> (OPTIONAL)</span>
			</label>

			<OaoTooltip
				v-if="tooltip && filteredInputConfig"
				:description="filteredInputConfig.helpText"
				:title="filteredInputConfig.helpTextTitle"
				isSmall
			/>
		</header>

		<CurrencyInput
			:id="inputId"
			:name="name || label"
			type="tel"
			:currency="currency"
			:precision="precision"
			:placeholder="placeholder"
			:distraction-free="false"
			:auto-decimal-mode="autoDecimalMode"
			:disabled="disabled"
			v-model="innerValue"
			:class="{ 'has-error': errors.length > 0, 'is-small': isSmall }"
			@change="$emit('change', $event)"
			@blur="$emit('blur', $event)"
			@focus="$emit('focus', $event)"
		/>

		<p v-if="errors.length > 0" class="error-message">{{ errors[0] }}</p>
	</ValidationProvider>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import { CurrencyInput } from 'vue-currency-input';
import { mapGetters } from 'vuex';
import OaoTooltip from '@/components/OaoTooltip';

export default {
	name: 'OaoFormCurrencyInput',
	components: {
		ValidationProvider,
		CurrencyInput,
		OaoTooltip
	},
	props: {
		value: {
			required: true
		},
		label: {
			type: String,
			required: true
		},
		name: {
			type: String,
			required: false
		},
		currency: {
			type: String,
			required: false,
			default: 'USD'
		},
		precision: {
			type: Number,
			required: false,
			default: 0
		},
		placeholder: {
			type: String,
			required: false,
			default: '$0'
		},
		autoDecimalMode: {
			type: Boolean,
			required: false,
			default: true
		},
		disabled: {
			type: Boolean,
			required: false,
			default: false
		},
		validationRules: {
			type: [String, Object],
			required: false
		},
		isOptional: {
			type: Boolean,
			required: false,
			default: false
		},
		validationId: {
			type: String,
			required: false
		},
		isSmall: {
			type: Boolean,
			required: false,
			default: false
		},
		id: {
			type: [String, Number],
			required: false
		},
		tooltip: {
			type: Boolean,
			required: false,
			default: false
		},
		tooltipTypeId: {
			type: Number,
			required: false
		}
	},
	computed: {
		...mapGetters('config', ['getInputConfig']),
		inputId() {
			return this.id || `${this.label.replace(/\s/g, '')}-${this._uid}`;
		},
		innerValue: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit('input', val);
			}
		},
		filteredInputConfig() {
			return this.getInputConfig(this.tooltipTypeId);
		}
	}
};
</script>

<style lang="scss" scoped>
.currency-field-container {
	&:not(:last-child) {
		margin-bottom: 1rem;
	}

	.input-header {
		display: flex;
		flex-direction: row;
		gap: 0.5rem;

		label {
			display: block;
			color: var(--text-primary);
			font-size: 0.8rem;
			margin-bottom: 0.5rem;
			font-weight: 400;
			text-transform: uppercase;

			&.has-error {
				color: var(--danger);
			}
		}
	}

	::v-deep input {
		box-sizing: border-box;
		width: 100%;
		border: none;
		border-bottom: 1.5px solid rgb(215, 215, 215);
		background-color: transparent;
		font-size: 1.25rem;
		padding: 0.25rem;

		&::placeholder {
			color: var(--lightGrey);
			font-style: oblique;
		}

		&.has-error {
			border-bottom: 1.5px solid var(--danger);
		}

		&:disabled {
			color: var(--lightGrey);
			cursor: not-allowed;
		}

		&.is-small {
			width: auto;
			min-width: 12rem;
		}
	}

	.error-message {
		color: var(--danger);
		font-size: 0.85rem;
		margin-top: 0.25rem;
	}
}
</style>
